// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';
import { Table, Card, Input, Select, Button, Space, message, DatePicker, Row, Col, Statistic, Tooltip } from 'antd';
import { SearchOutlined, FilterOutlined, DownloadOutlined, ClearOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Title } from '../components/design-system/typography';
import { StatusPill } from '../components/design-system/StatusPill';
import { colors, spacing } from '../components/design-system/tokens';
import dayjs, { Dayjs } from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Types
interface ShipmentRow {
  id: string;
  ref: string;
  contractRef: string;
  contractDate: string | null;
  supplier: string;
  sellerRef: string;
  buyer: string;
  goods: string;
  numUnits: number;
  lbsUnit: number;
  totalLbs: number;
  price: number;
  totalSale: number;
  commission: string;
  shipStart: string;
  shipEnd: string;
  basis: string;
  bookingNumber: string;
  etd: string | null;
  eta: string | null;
  status: string;
  containerCount: number;
  invoiceDate: string | null;
  invoiceNum: string;
  invoiceAmount: number | null;
  cropYear: string;
  commissionEst: number;
  rtEst: number;
  cwEst: number;
  commissionActual: number | null;
  rtActual: number | null;
  cwActual: number | null;
  marginActual: number;
  rtMarginActual: number;
  cwMarginActual: number;
  paymentDate: string | null;
  isSubtotalRow?: boolean;
}

interface SupplierTotals {
  numUnits: number;
  totalLbs: number;
  totalSale: number;
  commissionEst: number;
  rtEst: number;
  cwEst: number;
  commissionActual: number;
  rtActual: number;
  cwActual: number;
  marginActual: number;
  rtMarginActual: number;
  cwMarginActual: number;
}

interface ShipmentsResponse {
  shipments: ShipmentRow[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
  totals: {
    totalShipments: number;
    totalLbs: number;
    totalSale: number;
    inTransitCount: number;
  };
}

interface Filters {
  search: string;
  cropYear: string;
  status: string[];
  supplierId: string;
  buyerId: string;
  dateRange: [Dayjs | null, Dayjs | null] | null;
  dateType: 'etd' | 'eta';
  commodityId: string;
  sortField: string;
  sortOrder: 'asc' | 'desc';
}

// Get current crop year (single year, e.g., 2025)
function getCurrentCropYear(): string {
  return new Date().getFullYear().toString();
}

// Generate crop year options (single years)
function getCropYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const options: string[] = [];
  for (let i = currentYear + 1; i >= currentYear - 10; i--) {
    options.push(i.toString());
  }
  return options;
}

// API function to fetch shipments with filters
const fetchShipments = async (filters: Filters, page: number, pageSize: number): Promise<ShipmentsResponse> => {
  const token = localStorage.getItem('atlas_token') || localStorage.getItem('auth_token');
  if (!token) throw new Error('No authentication token');

  const params = new URLSearchParams();
  params.append('limit', pageSize.toString());
  params.append('offset', ((page - 1) * pageSize).toString());

  if (filters.search) params.append('search', filters.search);
  if (filters.cropYear) params.append('cropYear', filters.cropYear);
  if (filters.status?.length) params.append('status', filters.status.join(','));
  if (filters.supplierId) params.append('supplierId', filters.supplierId);
  if (filters.buyerId) params.append('buyerId', filters.buyerId);
  if (filters.commodityId) params.append('commodityId', filters.commodityId);
  if (filters.sortField) params.append('sortField', filters.sortField);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  // Date range filter
  if (filters.dateRange?.[0] && filters.dateRange?.[1]) {
    const dateKey = filters.dateType === 'eta' ? 'eta' : 'etd';
    params.append(`${dateKey}From`, filters.dateRange[0].format('YYYY-MM-DD'));
    params.append(`${dateKey}To`, filters.dateRange[1].format('YYYY-MM-DD'));
  }

  const response = await fetch(`/api/v1/shipments?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch shipments');
  }

  const data = await response.json();

  // Transform API response to our row format
  const shipments: ShipmentRow[] = (data.shipments || []).map((s: any) => {
    const containers = s.containers || [];
    const goodsSummary = s.goods_summary || {};
    const commission = s.commission || {};
    const margin = s.margin || {};

    return {
      id: s.id,
      ref: s.reference || '',
      contractRef: s.contract?.reference || '',
      contractDate: s.contract?.date || null,
      supplier: s.contract?.supplier?.name || s.contract?.supplier?.code || '',
      sellerRef: s.seller_ref || '',
      buyer: s.contract?.buyer?.name || s.contract?.buyer?.code || '',
      goods: goodsSummary.description || '',
      numUnits: goodsSummary.total_units || 0,
      lbsUnit: goodsSummary.total_units > 0 ? Math.round(goodsSummary.total_lbs / goodsSummary.total_units) : 0,
      totalLbs: goodsSummary.total_lbs || 0,
      price: goodsSummary.price || 0,
      totalSale: goodsSummary.total_sale || 0,
      commission: s.contract?.commission_str || '',
      shipStart: s.start || '',
      shipEnd: s.end || '',
      basis: s.basis || '',
      bookingNumber: s.booking_number || '',
      etd: s.etd,
      eta: s.eta,
      status: (s.status || 'planned').toUpperCase(),
      containerCount: containers.length,
      invoiceDate: s.invoice?.date || null,
      invoiceNum: s.invoice?.number || '',
      invoiceAmount: s.invoice?.amount || null,
      cropYear: s.contract?.crop_year || '',
      // Commission data
      commissionEst: commission.estimated || 0,
      rtEst: commission.rt_est || 0,
      cwEst: commission.cw_est || 0,
      commissionActual: commission.actual,
      rtActual: commission.rt_actual,
      cwActual: commission.cw_actual,
      // Margin data (buyerPrice - supplierPrice spread)
      marginActual: margin.total || 0,
      rtMarginActual: margin.rt || 0,
      cwMarginActual: margin.cw || 0,
      // Payment date
      paymentDate: s.payment_date || null,
    };
  });

  // Use totals from API response
  const totals = data.totals || {
    totalShipments: data.pagination?.total || shipments.length,
    totalLbs: 0,
    totalSale: 0,
    inTransitCount: shipments.filter((s: ShipmentRow) => s.status === 'IN_TRANSIT').length,
  };

  return {
    shipments,
    pagination: data.pagination || { total: shipments.length, limit: pageSize, offset: 0 },
    totals,
  };
};

// Fetch filter options
const fetchFilterOptions = async () => {
  const token = localStorage.getItem('atlas_token') || localStorage.getItem('auth_token');
  if (!token) return { suppliers: [], buyers: [], commodities: [] };

  try {
    const [suppliersRes, buyersRes, commoditiesRes] = await Promise.all([
      fetch('/api/v1/suppliers?limit=200', {
        headers: { 'Authorization': `Bearer ${token}` },
      }),
      fetch('/api/v1/buyers?limit=200', {
        headers: { 'Authorization': `Bearer ${token}` },
      }),
      fetch('/api/v1/commodities', {
        headers: { 'Authorization': `Bearer ${token}` },
      }),
    ]);

    const [suppliers, buyers, commodities] = await Promise.all([
      suppliersRes.ok ? suppliersRes.json() : { suppliers: [] },
      buyersRes.ok ? buyersRes.json() : { buyers: [] },
      commoditiesRes.ok ? commoditiesRes.json() : { commodities: [] },
    ]);

    return {
      suppliers: suppliers.suppliers || suppliers || [],
      buyers: buyers.buyers || buyers || [],
      commodities: commodities.commodities || commodities || [],
    };
  } catch (error) {
    console.error('Error fetching filter options:', error);
    return { suppliers: [], buyers: [], commodities: [] };
  }
};

const rowHighlightStyles = `
  .row-paid td { background-color: #e6f7ff !important; }
  .row-paid:hover td { background-color: #bae7ff !important; }
  .row-shipped td { background-color: #fffbe6 !important; }
  .row-shipped:hover td { background-color: #fff1b8 !important; }
  .row-subtotal td { background-color: #f0f0f0 !important; font-weight: 600 !important; }
  .row-subtotal:hover td { background-color: #e8e8e8 !important; }
`;

const MasterTrackingSheet: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [filters, setFilters] = useState<Filters>({
    search: '',
    cropYear: getCurrentCropYear(),
    status: [],
    supplierId: '',
    buyerId: '',
    dateRange: null,
    dateType: 'etd',
    commodityId: '',
    sortField: 'ref',
    sortOrder: 'asc',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [exporting, setExporting] = useState(false);
  const [showSupplierTotals, setShowSupplierTotals] = useState(true);

  // Fetch data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['masterTracking', filters, page, pageSize],
    queryFn: () => fetchShipments(filters, page, pageSize),
  });

  const { data: filterOptions } = useQuery({
    queryKey: ['filterOptions'],
    queryFn: fetchFilterOptions,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Shipments come pre-sorted from the API
  const rawShipments = data?.shipments || [];
  const total = data?.pagination?.total || 0;

  // Group shipments by supplier and insert subtotal rows
  const shipments = useMemo(() => {
    if (!showSupplierTotals || rawShipments.length === 0) {
      return rawShipments;
    }

    const result: ShipmentRow[] = [];
    let currentSupplier = '';
    let supplierTotals: SupplierTotals = {
      numUnits: 0, totalLbs: 0, totalSale: 0,
      commissionEst: 0, rtEst: 0, cwEst: 0,
      commissionActual: 0, rtActual: 0, cwActual: 0,
      marginActual: 0, rtMarginActual: 0, cwMarginActual: 0,
    };
    let shipmentCount = 0;

    const createSubtotalRow = (supplier: string, totals: SupplierTotals, count: number): ShipmentRow => ({
      id: `subtotal-${supplier}`,
      ref: `SUBTOTAL: ${supplier} (${count} shipments)`,
      contractRef: '', contractDate: null, supplier, sellerRef: '', buyer: '',
      goods: '', numUnits: totals.numUnits, lbsUnit: 0, totalLbs: totals.totalLbs,
      price: 0, totalSale: totals.totalSale, commission: '', shipStart: '', shipEnd: '',
      basis: '', bookingNumber: '', etd: null, eta: null, status: '', containerCount: 0,
      invoiceDate: null, invoiceNum: '', invoiceAmount: null, cropYear: '',
      commissionEst: totals.commissionEst, rtEst: totals.rtEst, cwEst: totals.cwEst,
      commissionActual: totals.commissionActual, rtActual: totals.rtActual, cwActual: totals.cwActual,
      marginActual: totals.marginActual, rtMarginActual: totals.rtMarginActual, cwMarginActual: totals.cwMarginActual,
      paymentDate: null, isSubtotalRow: true,
    });

    for (const shipment of rawShipments) {
      if (currentSupplier && shipment.supplier !== currentSupplier) {
        result.push(createSubtotalRow(currentSupplier, supplierTotals, shipmentCount));
        supplierTotals = {
          numUnits: 0, totalLbs: 0, totalSale: 0,
          commissionEst: 0, rtEst: 0, cwEst: 0,
          commissionActual: 0, rtActual: 0, cwActual: 0,
          marginActual: 0, rtMarginActual: 0, cwMarginActual: 0,
        };
        shipmentCount = 0;
      }

      currentSupplier = shipment.supplier;
      shipmentCount++;
      supplierTotals.numUnits += shipment.numUnits || 0;
      supplierTotals.totalLbs += shipment.totalLbs || 0;
      supplierTotals.totalSale += shipment.totalSale || 0;
      supplierTotals.commissionEst += shipment.commissionEst || 0;
      supplierTotals.rtEst += shipment.rtEst || 0;
      supplierTotals.cwEst += shipment.cwEst || 0;
      supplierTotals.commissionActual += shipment.commissionActual || 0;
      supplierTotals.rtActual += shipment.rtActual || 0;
      supplierTotals.cwActual += shipment.cwActual || 0;
      supplierTotals.marginActual += shipment.marginActual || 0;
      supplierTotals.rtMarginActual += shipment.rtMarginActual || 0;
      supplierTotals.cwMarginActual += shipment.cwMarginActual || 0;

      result.push(shipment);
    }

    if (currentSupplier && shipmentCount > 0) {
      result.push(createSubtotalRow(currentSupplier, supplierTotals, shipmentCount));
    }

    return result;
  }, [rawShipments, showSupplierTotals]);

  // Use totals from API (calculated across ALL matching records, not just page)
  const totals = data?.totals || { totalShipments: 0, totalLbs: 0, totalSale: 0, inTransitCount: 0 };

  // Handle error
  if (error) {
    message.error('Failed to load shipments');
  }

  // Handle filter changes
  const handleFilterChange = useCallback((key: keyof Filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page on filter change
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      cropYear: getCurrentCropYear(),
      status: [],
      supplierId: '',
      buyerId: '',
      dateRange: null,
      dateType: 'etd',
      commodityId: '',
      sortField: 'ref',
      sortOrder: 'asc',
    });
    setPage(1);
  }, []);

  // Handle export
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('atlas_token') || localStorage.getItem('auth_token');
      if (!token) {
        message.error('Authentication required');
        return;
      }

      const exportFilters: Record<string, unknown> = {};
      if (filters.status?.length) exportFilters.status = filters.status[0];
      if (filters.supplierId) exportFilters.supplierId = filters.supplierId;
      if (filters.buyerId) exportFilters.buyerId = filters.buyerId;
      if (filters.dateRange?.[0]) exportFilters.etdFrom = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) exportFilters.etdTo = filters.dateRange[1].format('YYYY-MM-DD');

      const response = await fetch('/api/v1/export/excel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportType: 'master-tracking',
          filters: exportFilters,
        }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Download the file
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
        || `master-tracking-${dayjs().format('YYYY-MM-DD')}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Export downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  }, [filters]);

  // Handle table changes
  const handleTableChange = useCallback((
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ShipmentRow> | SorterResult<ShipmentRow>[]
  ) => {
    setPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 50);

    // Handle sorting - update filters to trigger API call with new sort
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (singleSorter?.field) {
      setFilters(prev => ({
        ...prev,
        sortField: singleSorter.field as string,
        sortOrder: singleSorter.order === 'descend' ? 'desc' : 'asc',
      }));
    }
  }, []);

  // Navigate to contract
  const handleRowClick = useCallback((record: ShipmentRow) => {
    if (record.contractRef) {
      navigate(`/contracts/${record.contractRef}`);
    }
  }, [navigate]);

  // Determine row status for highlighting
  // Blue = paid (commission or margin received)
  // Yellow = shipped (has invoice or booking)
  // White = pending (default)
  const getRowClassName = useCallback((record: ShipmentRow): string => {
    if (record.isSubtotalRow) return 'row-subtotal';
    
    const isPaid = (record.commissionActual !== null && record.commissionActual > 0) || 
                   (record.marginActual > 0);
    const isShipped = record.invoiceNum || record.bookingNumber || record.invoiceDate;
    
    if (isPaid) return 'row-paid';
    if (isShipped) return 'row-shipped';
    return '';
  }, []);

  // Format helpers
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return dayjs(dateString).format('MMM D, YYYY');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const getStatusVariant = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'DELIVERED': return 'positive';
      case 'IN_TRANSIT': return 'warning';
      case 'ARRIVED': return 'positive';
      case 'PLANNED': return 'neutral';
      case 'CANCELLED': return 'negative';
      default: return 'neutral';
    }
  };

  // Common cell style to prevent line wrapping
  const cellStyle: React.CSSProperties = { whiteSpace: 'nowrap' };

  // Table columns - matching legacy master.php format
  const columns: ColumnsType<ShipmentRow> = [
    {
      title: 'Ref',
      dataIndex: 'ref',
      key: 'ref',
      width: 110,
      fixed: 'left',
      sorter: true,
      sortOrder: filters.sortField === 'ref' ? (filters.sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      defaultSortOrder: 'ascend',
      render: (text: string, record: ShipmentRow) => {
        if (record.isSubtotalRow) {
          return <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>;
        }
        return (
          <Tooltip title="Click to view contract">
            <a
              onClick={(e) => { e.stopPropagation(); handleRowClick(record); }}
              style={{ color: colors.primary.default, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {text || record.contractRef || '-'}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'contractDate',
      key: 'contractDate',
      width: 100,
      sorter: true,
      sortOrder: filters.sortField === 'contractDate' ? (filters.sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (val: string | null) => <span style={cellStyle}>{formatDate(val)}</span>,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 120,
      ellipsis: true,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'Seller#',
      dataIndex: 'sellerRef',
      key: 'sellerRef',
      width: 90,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer',
      key: 'buyer',
      width: 120,
      sorter: true,
      sortOrder: filters.sortField === 'buyer' ? (filters.sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      ellipsis: true,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'Goods',
      dataIndex: 'goods',
      key: 'goods',
      width: 140,
      ellipsis: true,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'CS',
      dataIndex: 'numUnits',
      key: 'numUnits',
      width: 60,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? formatNumber(value) : '-'}</span>,
    },
    {
      title: '#',
      dataIndex: 'lbsUnit',
      key: 'lbsUnit',
      width: 55,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value || '-'}</span>,
    },
    {
      title: 'Tot#',
      dataIndex: 'totalLbs',
      key: 'totalLbs',
      width: 85,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? formatNumber(value) : '-'}</span>,
    },
    {
      title: 'P',
      dataIndex: 'price',
      key: 'price',
      width: 70,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? `$${value.toFixed(3)}` : '-'}</span>,
    },
    {
      title: 'Tot$',
      dataIndex: 'totalSale',
      key: 'totalSale',
      width: 95,
      align: 'right',
      sorter: true,
      sortOrder: filters.sortField === 'totalSale' ? (filters.sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (value: number) => <span style={cellStyle}>{value ? `$${formatNumber(value)}` : '-'}</span>,
    },
    {
      title: 'C',
      dataIndex: 'commission',
      key: 'commission',
      width: 80,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'From',
      dataIndex: 'shipStart',
      key: 'shipStart',
      width: 80,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'To',
      dataIndex: 'shipEnd',
      key: 'shipEnd',
      width: 80,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'Basis',
      dataIndex: 'basis',
      key: 'basis',
      width: 65,
      render: (text: string) => <span style={cellStyle}>{text?.split(' ')[0] || '-'}</span>,
    },
    {
      title: 'Booking#',
      dataIndex: 'bookingNumber',
      key: 'bookingNumber',
      width: 100,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'Inv Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 100,
      sorter: true,
      sortOrder: filters.sortField === 'invoiceDate' ? (filters.sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (val: string | null) => <span style={cellStyle}>{formatDate(val)}</span>,
    },
    {
      title: 'Inv No.',
      dataIndex: 'invoiceNum',
      key: 'invoiceNum',
      width: 90,
      render: (text: string) => <span style={cellStyle}>{text || '-'}</span>,
    },
    {
      title: 'Inv Amt',
      dataIndex: 'invoiceAmount',
      key: 'invoiceAmount',
      width: 95,
      align: 'right',
      render: (value: number | null) => <span style={cellStyle}>{value ? `$${formatNumber(value)}` : '-'}</span>,
    },
    {
      title: 'C-Rcvd',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 100,
      render: (val: string | null) => <span style={cellStyle}>{formatDate(val)}</span>,
    },
    {
      title: 'C-Est',
      dataIndex: 'commissionEst',
      key: 'commissionEst',
      width: 85,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'RT',
      dataIndex: 'rtEst',
      key: 'rtEst',
      width: 80,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'CW',
      dataIndex: 'cwEst',
      key: 'cwEst',
      width: 80,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'C Act',
      dataIndex: 'commissionActual',
      key: 'commissionActual',
      width: 85,
      align: 'right',
      render: (value: number | null) => <span style={cellStyle}>{value !== null ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'RT Act',
      dataIndex: 'rtActual',
      key: 'rtActual',
      width: 80,
      align: 'right',
      render: (value: number | null) => <span style={cellStyle}>{value !== null ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'CW Act',
      dataIndex: 'cwActual',
      key: 'cwActual',
      width: 80,
      align: 'right',
      render: (value: number | null) => <span style={cellStyle}>{value !== null ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'M Act',
      dataIndex: 'marginActual',
      key: 'marginActual',
      width: 85,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'RT Act',
      dataIndex: 'rtMarginActual',
      key: 'rtMarginActual',
      width: 80,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
    {
      title: 'CW Act',
      dataIndex: 'cwMarginActual',
      key: 'cwMarginActual',
      width: 80,
      align: 'right',
      render: (value: number) => <span style={cellStyle}>{value ? `$${formatNumber(value, 2)}` : '-'}</span>,
    },
  ];

  // Status options
  const statusOptions = [
    { value: 'PLANNED', label: 'Planned' },
    { value: 'IN_TRANSIT', label: 'In Transit' },
    { value: 'ARRIVED', label: 'Arrived' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  // Calculate table summary
  const tableSummary = useMemo(() => {
    const totalUnits = shipments.reduce((sum, s) => sum + (s.numUnits || 0), 0);
    const totalLbs = shipments.reduce((sum, s) => sum + (s.totalLbs || 0), 0);
    const totalSale = shipments.reduce((sum, s) => sum + (s.totalSale || 0), 0);
    const totalCommissionEst = shipments.reduce((sum, s) => sum + (s.commissionEst || 0), 0);
    const totalRtEst = shipments.reduce((sum, s) => sum + (s.rtEst || 0), 0);
    const totalCwEst = shipments.reduce((sum, s) => sum + (s.cwEst || 0), 0);
    const totalCommissionActual = shipments.reduce((sum, s) => sum + (s.commissionActual || 0), 0);
    const totalRtActual = shipments.reduce((sum, s) => sum + (s.rtActual || 0), 0);
    const totalCwActual = shipments.reduce((sum, s) => sum + (s.cwActual || 0), 0);
    const totalMarginActual = shipments.reduce((sum, s) => sum + (s.marginActual || 0), 0);
    const totalRtMarginActual = shipments.reduce((sum, s) => sum + (s.rtMarginActual || 0), 0);
    const totalCwMarginActual = shipments.reduce((sum, s) => sum + (s.cwMarginActual || 0), 0);
    return {
      totalUnits,
      totalLbs,
      totalSale,
      totalCommissionEst,
      totalRtEst,
      totalCwEst,
      totalCommissionActual,
      totalRtActual,
      totalCwActual,
      totalMarginActual,
      totalRtMarginActual,
      totalCwMarginActual,
    };
  }, [shipments]);

  return (
    <div style={{
      background: colors.background.primary,
      minHeight: 'calc(100vh - 112px)',
      padding: spacing['2xl']
    }}>
      <style>{rowHighlightStyles}</style>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{
          marginBottom: spacing['2xl'],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <Title>Master Tracking Sheet</Title>
            <p style={{
              color: colors.text.secondary,
              fontSize: '16px',
              marginTop: spacing.sm
            }}>
              Comprehensive view of all shipments with filtering and export
            </p>
          </div>
          <Space>
            <Button
              icon={showSupplierTotals ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={() => setShowSupplierTotals(!showSupplierTotals)}
              size="large"
            >
              {showSupplierTotals ? 'Hide' : 'Show'} Supplier Totals
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              size="large"
            >
              Export to Excel
            </Button>
          </Space>
        </div>

        {/* Summary Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: spacing['2xl'] }}>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Card size="small">
              <Statistic
                title="Total Shipments"
                value={totals.totalShipments}
                valueStyle={{ color: colors.text.primary }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Card size="small">
              <Statistic
                title="In Transit"
                value={totals.inTransitCount}
                valueStyle={{ color: colors.status.warning }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Card size="small">
              <Statistic
                title="Total Lbs"
                value={totals.totalLbs}
                valueStyle={{ color: colors.text.primary }}
                formatter={(value) => formatNumber(Number(value))}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <Card size="small">
              <Statistic
                title="Total Sale"
                value={totals.totalSale}
                prefix="$"
                precision={2}
                valueStyle={{ color: colors.status.positive }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginBottom: spacing.lg }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8} lg={6}>
              <Search
                placeholder="Search contracts, suppliers..."
                allowClear
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={12} md={4} lg={3}>
              <Select
                placeholder="Crop Year"
                value={filters.cropYear || undefined}
                onChange={(value) => handleFilterChange('cropYear', value)}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="">All Years</Option>
                {getCropYearOptions().map(year => (
                  <Option key={year} value={year}>{year}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={4} lg={3}>
              <Select
                placeholder="Status"
                mode="multiple"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                style={{ width: '100%' }}
                maxTagCount={1}
                allowClear
              >
                {statusOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={8} lg={5}>
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  value={filters.dateType}
                  onChange={(value) => handleFilterChange('dateType', value)}
                  style={{ width: '80px' }}
                >
                  <Option value="etd">ETD</Option>
                  <Option value="eta">ETA</Option>
                </Select>
                <RangePicker
                  value={filters.dateRange}
                  onChange={(dates) => handleFilterChange('dateRange', dates)}
                  style={{ flex: 1 }}
                  format="MMM D"
                />
              </Space.Compact>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Select
                placeholder="Supplier"
                value={filters.supplierId || undefined}
                onChange={(value) => handleFilterChange('supplierId', value)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                }
                allowClear
              >
                {(filterOptions?.suppliers || []).map((s: any) => (
                  <Option key={s.id} value={s.id}>{s.shortName || s.name}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Select
                placeholder="Buyer"
                value={filters.buyerId || undefined}
                onChange={(value) => handleFilterChange('buyerId', value)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                }
                allowClear
              >
                {(filterOptions?.buyers || []).map((b: any) => (
                  <Option key={b.id} value={b.id}>{b.shortName || b.name}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={4} lg={2}>
              <Button
                icon={<ClearOutlined />}
                onClick={clearFilters}
                style={{ width: '100%' }}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Data Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={shipments}
            rowKey="id"
            loading={isLoading}
            onChange={handleTableChange}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} shipments`,
            }}
            scroll={{ x: 2800 }}
            size="small"
            rowClassName={getRowClassName}
            onRow={(record) => ({
              onClick: () => !record.isSubtotalRow && handleRowClick(record),
              style: { cursor: record.isSubtotalRow ? 'default' : 'pointer' },
            })}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: colors.background.secondary, fontWeight: 600 }}>
                  <Table.Summary.Cell index={0} colSpan={6}>
                    TOTALS (Page)
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    {formatNumber(tableSummary.totalUnits)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} />
                  <Table.Summary.Cell index={8} align="right">
                    {formatNumber(tableSummary.totalLbs)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} />
                  <Table.Summary.Cell index={10} align="right">
                    ${formatNumber(tableSummary.totalSale)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} colSpan={9} />
                  <Table.Summary.Cell index={20} align="right">
                    ${formatNumber(tableSummary.totalCommissionEst, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={21} align="right">
                    ${formatNumber(tableSummary.totalRtEst, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={22} align="right">
                    ${formatNumber(tableSummary.totalCwEst, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={23} align="right">
                    ${formatNumber(tableSummary.totalCommissionActual, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={24} align="right">
                    ${formatNumber(tableSummary.totalRtActual, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={25} align="right">
                    ${formatNumber(tableSummary.totalCwActual, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={26} align="right">
                    ${formatNumber(tableSummary.totalMarginActual, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={27} align="right">
                    ${formatNumber(tableSummary.totalRtMarginActual, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={28} align="right">
                    ${formatNumber(tableSummary.totalCwMarginActual, 2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
      </div>
    </div>
  );
};

export default MasterTrackingSheet;
